// @flow
import React, { PureComponent, Fragment } from 'react';

import Breadcrumb from 'components/fragments/Breadcrumb';
import Sidebar from 'components/sidebar/Sidebar';
import ContactForm7 from 'containers/contactForm7/ContactForm7Container';
import ContactList from 'containers/contact/ContactListContainer';
import CF7FormObjectSelector from 'containers/contactForm7/CF7FormObjectSelectorContainer';
import LiveScore from 'containers/fragments/LiveScoreContainer';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { ThemeType } from 'types/Faq';
import type { BlocContent } from 'types/NewsletterBloc';
import { getMetaBySlug } from 'utils/seoUtils';

import './contact-css.css';

export type StateProps = {
  faqThemes?: Array<ThemeType>, // faqThemes
  title: string,
  excerpt: string,
  sidebar_content: BlocContent[],
  featured_media: {
    legend: string,
    src: string
  },
  placeholder: string,
  banniere: string,
  clubName: string,
  rencontresLive: number
};

export type DispatchProps = {
  fetchThemes: () => void,
  fetchQuestions: () => void,
  fetchPageList: (slug: string) => void
};

type Props = {
  location: {
    pathname: string,
    search: string
  },
  match: {
    params: {
      slugTheme: string
    }
  }
} & DispatchProps &
  StateProps;

type State = {
  currentThemeTitle: string,
  faqTheme: string,
  formId: number
};

class ContactWrapper extends PureComponent<Props, State> {
  static defaultProps = {
    faqThemes: []
  };

  state: State = {
    currentThemeTitle: '',
    faqTheme: '',
    formId: -1
  };

  componentDidMount() {
    const {
      fetchPageList,
      fetchThemes,
      fetchQuestions,
      match: {
        params: { slugTheme }
      },
      faqThemes
    } = this.props;

    //* fetch necessary data
    fetchThemes();
    fetchQuestions();
    fetchPageList('contact');

    //* faq sections
    const faqTheme = faqThemes && faqThemes.find(theme => theme.slug === slugTheme);

    if (faqTheme) {
      this.setState({ faqTheme: faqTheme.slug });
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      match: {
        params: { slugTheme }
      },
      faqThemes
    } = this.props;

    //* faq sections
    const faqTheme = faqThemes && faqThemes.find(theme => theme.slug === slugTheme);

    if (faqTheme) {
      this.setState({ faqTheme: faqTheme.slug });
    } else {
      this.setState({ faqTheme: '' });
    }
  }

  setCurrentThemeTitle = (currentThemeTitle: string) => {
    this.setState({ currentThemeTitle });
  };

  updateFormIdState = (formId: number) => {
    this.setState({ formId });
  };

  render() {
    const {
      banniere,
      clubName,
      excerpt,
      featured_media: { src },
      location: { search },
      placeholder,
      rencontresLive,
      sidebar_content,
      faqThemes,
      title,
      match: {
        params: { slugTheme }
      }
    } = this.props;
    const { currentThemeTitle, faqTheme, formId } = this.state;

    // banner
    let bannerClassName = 'banner banner--contact';
    if (rencontresLive) {
      bannerClassName = 'banner banner--contact banner--live-score';
    }

    // form
    const params = new URLSearchParams(search);
    let formSlug = slugTheme;
    const initialValues = {};
    params.forEach((value, key) => {
      if (key === 'q') {
        formSlug = value;
      } else {
        initialValues[key] = value;
      }
    });

    return (
      <Fragment>
        <section className={bannerClassName}>
          <div
            className="banner__thumbnail img-secondary"
            style={{ backgroundImage: `url(${src || banniere || placeholder})` }}
          />
          <div className="container">
            <Breadcrumb breadcrumbStyle="" elements={[{ name: 'Accueil', to: '/' }, 'Nous contacter']} isDark={true} />

            <h1 className="ft-h2">{title} </h1>

            <p className="banner__desc">{excerpt}</p>
          </div>

          <LiveScore />
        </section>

        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9">
                    <div className="page">
                      <CF7FormObjectSelector
                        formId={formId}
                        otherObjects={faqThemes}
                        setCurrentThemeTitle={this.setCurrentThemeTitle}
                        slug={formSlug}
                        updateFormIdState={this.updateFormIdState}
                      />
                      {!!faqTheme && <ContactList />}
                      <div className="form-contact block-contact__form">
                        <ContactForm7
                          currentThemeTitle={currentThemeTitle}
                          formId={formId}
                          initialValues={initialValues}
                          title={`Contacter le club ${clubName ? clubName : ''}`}
                          titleClass="mb-4"
                          titleTag="h3"
                          slugTheme={slugTheme}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <Sidebar sidebarContent={sidebar_content} />
          </div>
        </div>
      </Fragment>
    );
  }
}

export default withHelmet(ContactWrapper)((Helmet, pageProps) => {
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title(pageProps.clubName)}</title>
      {meta.description() ? <meta name={'description'} content={meta.description()} /> : ''}
      <meta name={'keywords'} content={meta.keywords(pageProps.clubName)} />
      <meta property={'og:title'} content={meta.title(pageProps.clubName)} />
      {meta.description() ? <meta property={'og:description'} content={meta.description()} /> : ''}
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
